/**
 * Get Started Checklist Constants
 *
 * Step definitions and mappings between backend flags and checklist steps.
 */

import type { CompanyPayment } from '@scio3/shared-types';
import type { ChecklistStep } from './types';
import { StepId } from './types';

/**
 * Step definitions for the onboarding checklist.
 * Order determines display order in the widget.
 */
export const CHECKLIST_STEPS: ChecklistStep[] = [
  {
    id: StepId.PAIR_SCREEN,
    titleKey: 'getStarted.steps.pairScreenAndContent.title',
    route: '/app/screenManagement',
    required: true,
    directionGroups: [
      {
        settingKey: 'firstDevicePaired',
        directions: [
          'getStarted.steps.pairScreenAndContent.directions.goToScreensPage',
          'getStarted.steps.pairScreenAndContent.directions.clickNewScreen',
          'getStarted.steps.pairScreenAndContent.directions.enterPairingCode',
        ],
      },
      {
        settingKey: 'firstScreenAssignmentEventFired',
        directions: ['getStarted.steps.pairScreenAndContent.directions.pushContent'],
      },
    ],
  },
  {
    id: StepId.CREATE_CONTENT,
    titleKey: 'getStarted.steps.createContent.title',
    route: '/app/assetManagement',
    required: true,
    directions: [
      'getStarted.steps.createContent.directions.goToAssetsPage',
      'getStarted.steps.createContent.directions.clickAddNew',
      'getStarted.steps.createContent.directions.createContent',
    ],
  },
  {
    id: StepId.CREATE_PLAYLIST,
    titleKey: 'getStarted.steps.createPlaylist.title',
    route: '/app/playlistManagement',
    required: false,
    directionGroups: [
      {
        settingKey: 'firstPlaylistCreated',
        directions: [
          'getStarted.steps.createPlaylist.directions.goToPlaylistsPage',
          'getStarted.steps.createPlaylist.directions.clickNewPlaylist',
        ],
      },
      {
        settingKey: 'firstPlaylistContentAdded',
        directions: ['getStarted.steps.createPlaylist.directions.addAssets'],
      },
    ],
  },
];

/**
 * Mapping from StepId to the backend payment flag field name(s).
 * A step is considered complete when ANY of its mapped flags is "triggered"
 * — flipping a single flag in Mongo cleans the whole step.
 */
export const STEP_FLAG_MAPPING: Record<StepId, (keyof CompanyPayment)[]> = {
  [StepId.PAIR_SCREEN]: ['firstDevicePaired', 'firstScreenAssignmentEventFired'],
  [StepId.CREATE_CONTENT]: ['firstContentCreated'],
  [StepId.CREATE_PLAYLIST]: ['firstPlaylistCreated'],
};

/** Value indicating a flag has not been triggered yet */
export const NOT_TRIGGERED = 'not_triggered';

/** Wistia video ID for the getting started video */
export const GETTING_STARTED_VIDEO_ID = 'fb0t3jowhv';

/**
 * Check if a flag value indicates the step is completed.
 * A step is complete if the flag is set AND is not 'not_triggered'.
 */
export function isFlagCompleted(value: string | undefined): boolean {
  return Boolean(value) && value !== NOT_TRIGGERED;
}
